import { useServices } from "@/services-context";
import { PrivacyPolicyDesktop } from "./privacy-policy-desktop";
import { PrivacyPolicyMobile } from "./privacy-policy-mobile";
import { LocationHook, RouteHook, useLocation, useRoute } from "preact-iso";
import { LayoutTypes, useLayoutType } from "@/util-react/layout";
import { Services } from "@/services/services";
import { pageSuspense, useSuspenseLoader } from "@/helpers/suspense";
import { StaticPagesType } from "@/context/static-pages";
import { SeoMarkupDataset } from "@/services/seo-markup.ts";
import { PageTags } from "@/elements-react/document-elements/page-tags.tsx";
import { Url } from "@/helpers/url.ts";

const fetchPageData = async (params: { services: Services; location: LocationHook; route: RouteHook }) => {
  const { services, location, route } = params;

  await services.initialize.prepare({
    location: location,
    route: route,
  });

  const registryUrl = Url.normalizeRoute(route.path);
  const alternates = await services.routes.resolveAlternates(registryUrl);

  if (!services.staticPages.has(StaticPagesType.privacyPolicy)) {
    await services.staticPages.fetchStaticPage(StaticPagesType.privacyPolicy);
  }

  return {
    type: "static",
    canonical: registryUrl,
    alternates: alternates,
  };
};

const getPageComponent = (layoutType: LayoutTypes) => {
  switch (layoutType) {
    case LayoutTypes.TYPE_DESKTOP:
      return PrivacyPolicyDesktop;
    case LayoutTypes.TYPE_MOBILE:
      return PrivacyPolicyMobile;
  }
};

export const PrivacyPolicyPage = pageSuspense(() => {
  const route = useRoute();
  const location = useLocation();
  const services = useServices();

  const seoMarkupDataset = useSuspenseLoader({
    key: "privacy-policy-route",
    loader: () =>
      fetchPageData({
        services: services,
        location: location,
        route: route,
      }),
  }) as SeoMarkupDataset;

  const { layoutType } = useLayoutType();

  const content = services.staticPages.getStaticPage(StaticPagesType.privacyPolicy);
  const PageComponent = getPageComponent(layoutType);

  return (
    <div style={{ display: "contents" }}>
      <PageComponent content={content} />
      <PageTags seo={seoMarkupDataset} />
    </div>
  );
});
